import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LandingHeader from '../Shared/LandingHeader';
import LandingFooter from '../Shared/LandingFooter';
import './HelpCenter.css';

const HelpCenter = () => {
    const { t } = useTranslation(['help', 'common']);
    const [search, setSearch] = useState('');

    const topics = [
        {
            icon: '🚀',
            title: t('help:topics.getting_started.title', 'Getting Started'),
            desc: t('help:topics.getting_started.desc', 'Set up your account, choose your board and class, and take your first test.'),
            articles: [
                { title: t('help:articles.create_account', 'How to create an account'), time: '2 min' },
                { title: t('help:articles.choose_board', 'Choosing your board and class'), time: '1 min' },
                { title: t('help:articles.first_test', 'Taking your first diagnostic test'), time: '3 min' },
                { title: t('help:articles.dashboard', 'Understanding your dashboard'), time: '4 min' },
            ],
        },
        {
            icon: '📊',
            title: t('help:topics.tests_reports.title', 'Tests & Reports'),
            desc: t('help:topics.tests_reports.desc', 'Learn how AI tests work, how to read your reports, and how to retake tests.'),
            articles: [
                { title: t('help:articles.how_tests_work', 'How the AI diagnostic test works'), time: '3 min' },
                { title: t('help:articles.read_report', 'Reading your AI Doctor Report'), time: '5 min' },
                { title: t('help:articles.retake_test', 'How to retake a chapter test'), time: '2 min' },
                { title: t('help:articles.scores', 'Understanding colour-coded scores'), time: '2 min' },
            ],
        },
        // ... abbreviated for the refactor to show the pattern
    ];

    const popularArticles = [
        { title: t('help:articles.first_test'), cat: t('help:topics.getting_started.title') },
        { title: t('help:articles.scores'), cat: t('help:topics.tests_reports.title') },
        // ... 
    ];

    const filteredTopics = search.trim()
        ? topics.map(t => ({
            ...t,
            articles: t.articles.filter(a =>
                a.title.toLowerCase().includes(search.toLowerCase())
            ),
        })).filter(t => t.articles.length > 0)
        : topics;

    return (
        <div className="hc-page">
            <LandingHeader activePage="help" />

            <main style={{ paddingTop: '80px' }}>
                {/* Hero */}
                <section className="hc-hero">
                    <div className="hc-container">
                        <div className="hc-hero-badge">{t('help:hero.badge')}</div>
                        <h1>{t('help:hero.title')} <span className="hc-gradient">{t('help:hero.titleHighlight')}</span></h1>
                        <p className="hc-hero-sub">
                            {t('help:hero.subtext')}
                        </p>

                        {/* Search */}
                        <div className="hc-search-wrap">
                            <span className="hc-search-icon">🔍</span>
                            <input
                                type="text"
                                className="hc-search"
                                placeholder={t('help:hero.searchPlaceholder')}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                            {search && (
                                <button className="hc-search-clear" onClick={() => setSearch('')}>✕</button>
                            )}
                        </div>

                        {/* Quick stats */}
                        <div className="hc-stats">
                            <span>📚 {t('help:hero.stats.articles')}</span>
                            <span>🕐 {t('help:hero.stats.response')}</span>
                            <span>✅ {t('help:hero.stats.resolved')}</span>
                        </div>
                    </div>
                    <div className="hc-hero-glow" />
                </section>

                {/* Popular Articles */}
                {!search && (
                    <section className="hc-popular-section">
                        <div className="hc-container">
                            <h2 className="hc-section-title">
                                <span>⚡</span> {t('help:popular.title')}
                            </h2>
                            <div className="hc-popular-grid">
                                {popularArticles.map((a, i) => (
                                    <Link to="/faq" key={i} className="hc-popular-card">
                                        <div className="hc-popular-title">{a.title}</div>
                                        <div className="hc-popular-cat">{a.cat}</div>
                                        <span className="hc-popular-arrow">→</span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* Topics Grid */}
                <section className="hc-topics-section">
                    <div className="hc-container">
                        {!search && (
                            <h2 className="hc-section-title">
                                <span>📖</span> {t('help:browse.title')}
                            </h2>
                        )}

                        {filteredTopics.length === 0 ? (
                            <div className="hc-empty">
                                <div className="hc-empty-icon">🤔</div>
                                <h3>{t('help:empty.title')}</h3>
                                <p>{t('help:empty.text')} <Link to="/contact">{t('help:empty.link')}</Link>.</p>
                            </div>
                        ) : (
                            <div className="hc-topics-grid">
                                {filteredTopics.map((topic, i) => (
                                    <div className="hc-topic-card" key={i}>
                                        <div className="hc-topic-header">
                                            <span className="hc-topic-icon">{topic.icon}</span>
                                            <div>
                                                <h3>{topic.title}</h3>
                                                <p className="hc-topic-desc">{topic.desc}</p>
                                            </div>
                                        </div>
                                        <ul className="hc-article-list">
                                            {topic.articles.map((art, j) => (
                                                <li key={j} className="hc-article-item">
                                                    <Link to="/faq" className="hc-article-link">
                                                        <span>{art.title}</span>
                                                        <span className="hc-article-meta">
                                                            <span className="hc-article-time">⏱ {art.time}</span>
                                                            <span className="hc-article-arrow">→</span>
                                                        </span>
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>

                {/* Contact strip */}
                <section className="hc-contact-strip">
                    <div className="hc-container hc-contact-grid">
                        <div className="hc-contact-card">
                            <div className="hc-contact-icon">💬</div>
                            <h3>{t('help:contact.chat.title')}</h3>
                            <p>{t('help:contact.chat.text')}</p>
                            <Link to="/contact" className="hc-contact-link">{t('help:contact.chat.link')}</Link>
                        </div>
                        <div className="hc-contact-card">
                            <div className="hc-contact-icon">📧</div>
                            <h3>{t('help:contact.email.title')}</h3>
                            <p>{t('help:contact.email.text')}</p>
                            <a href="mailto:support@skilltherapyai.com" className="hc-contact-link">
                                {t('help:contact.email.link')}
                            </a>
                        </div>
                        <div className="hc-contact-card">
                            <div className="hc-contact-icon">❓</div>
                            <h3>{t('help:contact.faq.title')}</h3>
                            <p>{t('help:contact.faq.text')}</p>
                            <Link to="/faq" className="hc-contact-link">{t('help:contact.faq.link')}</Link>
                        </div>
                    </div>
                </section>
            </main>

            <LandingFooter />
        </div>
    );
};

export default HelpCenter;
